import React, { useMemo } from "react";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "../ui/tooltip";
import { useDashboardFilters } from "@/contexts/DashboardFilters";
import { useCompset } from "@/contexts/CompsetContext";


// Heatmap color scale
export const getHeatmapColor = (value) => {
    if (value === null || value === undefined) return "bg-muted/30";
    if (value >= 90) return "bg-emerald-500";
    if (value >= 80) return "bg-emerald-400";
    if (value >= 70) return "bg-yellow-400";
    if (value >= 60) return "bg-orange-400";
    if (value >= 50) return "bg-orange-500";
    return "bg-red-500";
};

const getTextColor = (value) => {
    if (value === null || value === undefined) return "text-muted-foreground";
    return "text-white";
};


export default function AmenityHeatmap({ data}) {

    const { selectedCompetitors } = useDashboardFilters();
    const { competitors: activeCompetitors } = useCompset()


    const { hotels, amenities, heatmapData } = useMemo(() => {
        if (!data?.yourHotel) {
            return { hotels: [], amenities: [], heatmapData: [] };
        }

        const { yourHotel, competitors = [] } = data;

        const selectedComp =
            activeCompetitors?.map(comp => {
                if (selectedCompetitors.includes(comp?.id)) {
                    return comp?.name
                }
            })
                ?.filter(Boolean)

        const filteredCompetitors =
            selectedCompetitors.length == 0
                ? competitors
                : competitors?.filter(comp => selectedComp?.includes(comp?.name))


        const allHotels = [
            { ...yourHotel, isYourHotel: true },
            ...filteredCompetitors.map((c) => ({ ...c, isYourHotel: false })),
        ];        

        // Get top amenities
        const amenityTotals = new Map();

        allHotels.forEach((hotel) => {
            hotel?.amenities?.forEach((a) => {
                const current = amenityTotals.get(a?.name) || 0;
                amenityTotals.set(a?.name, current + (a?.total || 0));
            });
        });

        const topAmenities = Array.from(amenityTotals.entries())
            .sort((a, b) => b[1] - a[1])
            .map(([name]) => name);

        const heatmapMatrix = allHotels.map((hotel) => {
            const hotelData = {
                name:
                    hotel?.name?.length > 20
                        ? hotel?.name.substring(0, 18) + "..."
                        : hotel?.name,
                fullName: hotel?.name,
                isYourHotel: hotel?.isYourHotel,
                scores: {},
            };

            topAmenities.forEach((amenityName) => {
                const amenity = hotel?.amenities?.find(
                    (a) => a?.name === amenityName
                );

                hotelData.scores[amenityName] = amenity
                    ? amenity?.positive_percent
                    : null;
            });

            return hotelData;
        });

        return {
            hotels: allHotels.map((h) => ({
                name:
                    h?.name?.length > 20 ? h?.name.substring(0, 18) + "..." : h?.name,
                fullName: h?.name,
                isYourHotel: h?.isYourHotel,
            })),
            amenities: topAmenities,
            heatmapData: heatmapMatrix,
        };
    }, [data, selectedCompetitors]);


    // const [yourAvg, competitorAvg, bestAmenity, worstAmenity] = useMemo(() => {
    //     const yourHotelData = heatmapData.find(h => h.isYourHotel);
    //     const competitorData = heatmapData.filter(h => !h.isYourHotel);

    //     if (!yourHotelData) {
    //         return [0, 0, null, null];
    //     }

    //     const yourScores = Object.values(yourHotelData.scores).filter(v => v !== null);
    //     const yourAvg = yourScores.length > 0
    //         ? Math.round(yourScores.reduce((a, b) => a + b, 0) / yourScores.length)
    //         : 0;

    //     const allCompetitorScores = competitorData.flatMap(c =>
    //         Object.values(c.scores).filter(v => v !== null)
    //     );
    //     const competitorAvg = allCompetitorScores.length > 0
    //         ? Math.round(allCompetitorScores.reduce((a, b) => a + b, 0) / allCompetitorScores.length)
    //         : 0;

    //     // Find best and worst amenities for your hotel
    //     const yourAmenityScores = amenities.map(a => ({
    //         name: a,
    //         score: yourHotelData.scores[a],
    //         competitorAvg: Math.round(
    //             competitorData.map(c => c.scores[a]).filter(v => v !== null).reduce((a, b) => a + b, 0) /
    //             competitorData.filter(c => c.scores[a] !== null).length || 0
    //         )
    //     })).filter(a => a.score !== null);

    //     const bestAmenity = yourAmenityScores.sort((a, b) => b.score - a.score)[0];
    //     const worstAmenity = yourAmenityScores.sort((a, b) => a.score - b.score)[0];

    //     return [yourAvg, competitorAvg, bestAmenity, worstAmenity]

    // }, [heatmapData, amenities])

    if (!heatmapData.length) {
        return (
            <div className="text-center text-sm text-muted-foreground p-10">
                No data available
            </div>
        );
    }


    return (
        <TooltipProvider delayDuration={100}>
            <div className="space-y-4">

                {/* Legend */}
                <div className="flex md:items-center justify-between flex-col md:flex-row gap-4">

                    <div className="text-sm text-muted-foreground">
                        Positive sentiment scores across your competitive set
                    </div>

                    <div className="flex items-center gap-1 text-xs">

                        <span className="text-muted-foreground mr-2">Score:</span>

                        <div className="flex items-center gap-0.5">
                            <div className="w-6 h-4 bg-red-500 rounded-l" />
                            <div className="w-6 h-4 bg-orange-500" />
                            <div className="w-6 h-4 bg-orange-400" />
                            <div className="w-6 h-4 bg-yellow-400" />
                            <div className="w-6 h-4 bg-emerald-400" />
                            <div className="w-6 h-4 bg-emerald-500 rounded-r" />
                        </div>

                        <div className="flex gap-3 ml-2 text-muted-foreground">
                            <span>0%</span>
                            <span>100%</span>
                        </div>

                    </div>
                </div>


                {/* Heatmap */}
                <div className="overflow-x-auto overflow-y-visible pb-6">

                    <div className="min-w-[700px]">

                        {/* Header */}
                        <div className="flex">

                            <div className="w-36 flex-shrink-0 p-2 font-semibold text-xs text-muted-foreground uppercase tracking-wide">
                                Hotel
                            </div>

                            {amenities.map((amenity, i) => (
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <div
                                            key={i}
                                            className="flex-1 p-2 text-center font-medium text-xs text-muted-foreground truncate min-w-[60px]"
                                        >
                                            {amenity.length > 10
                                                ? amenity.substring(0, 6) + ".."
                                                : amenity}
                                        </div>
                                    </TooltipTrigger>
                                    <TooltipContent
                                        side="top"
                                        align="center"
                                        sideOffset={8}
                                        className="border bg-black/80 border-border rounded-lg shadow-xl"
                                    >
                                        <p>
                                            {amenity}
                                        </p>
                                    </TooltipContent>
                                </Tooltip>
                            ))}

                        </div>



                        {/* Rows */}
                        {heatmapData.map((hotel, rowIndex) => (

                            <div
                                key={rowIndex}
                                className={`flex items-center border-t border-border ${hotel?.isYourHotel ? "bg-primary/5" : ""
                                    }`}
                            >

                                {/* Hotel name */}
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <div
                                            className={`w-36 flex-shrink-0 p-2 text-sm font-medium truncate ${hotel?.isYourHotel
                                                ? "text-primary"
                                                : "text-foreground"
                                                }`}
                                        >
                                            {hotel?.name}
                                            {hotel?.isYourHotel && (
                                                <span className="ml-1 text-xs">(You)</span>
                                            )}
                                        </div>
                                    </TooltipTrigger>

                                    <TooltipContent
                                        side="top"
                                        align="center"
                                        sideOffset={8}
                                        className="border bg-black/80 border-border rounded-lg shadow-xl"
                                    >
                                        <p>
                                            {hotel?.fullName}
                                            {hotel?.isYourHotel && (
                                                <span className="ml-1 text-xs">(You)</span>
                                            )}
                                        </p>
                                    </TooltipContent>

                                </Tooltip>


                                {/* Cells */}
                                {amenities.map((amenity, colIndex) => {

                                    const value = hotel?.scores?.[amenity];

                                    return (
                                        <Tooltip key={colIndex}>
                                            <TooltipTrigger asChild>
                                                <div className="flex-1 p-1 min-w-[60px]">
                                                    <div
                                                        className={`h-10 rounded flex items-center justify-center text-xs font-bold
                            ${getHeatmapColor(value)}
                            ${getTextColor(value)}
                            transition-all duration-150 hover:scale-105 hover:shadow-lg cursor-pointer
                            ${value === null ? "opacity-30" : ""}`}
                                                    >
                                                        {value !== null && value !== undefined
                                                            ? `${value}%`
                                                            : "-"}
                                                    </div>

                                                </div>

                                            </TooltipTrigger>


                                            {value !== null && value !== undefined && (
                                                <TooltipContent
                                                    side="top"
                                                    align="center"
                                                    sideOffset={8}
                                                    className="bg-card border border-border rounded-lg shadow-xl p-3 min-w-[180px]"
                                                >

                                                    <p className="font-semibold text-foreground text-sm mb-1 whitespace-normal break-words max-w-[200px]">
                                                        {hotel.fullName}
                                                        {hotel.isYourHotel && (
                                                            <span className="text-primary ml-1">(You)</span>
                                                        )}
                                                    </p>

                                                    <p className="text-muted-foreground text-xs mb-2">
                                                        {amenity}
                                                    </p>

                                                    <div className="flex items-center gap-2">

                                                        <div
                                                            className={`w-3 h-3 rounded ${getHeatmapColor(
                                                                value
                                                            )}`}
                                                        />

                                                        <span className="font-bold text-foreground">
                                                            {value}%
                                                        </span>

                                                        <span className="text-xs text-muted-foreground">
                                                            positive sentiment
                                                        </span>

                                                    </div>

                                                </TooltipContent>
                                            )}
                                        </Tooltip>
                                    );
                                })}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Summary STats */}
                {/* <div className="grid grid-cols-4 gap-3 pt-2">
                    <>
                        <div className="p-3 rounded-lg bg-muted/50 text-center">
                            <p className="text-xs text-muted-foreground mb-1">Your Average</p>
                            <p className="text-2xl font-bold text-primary">{yourAvg}%</p>
                        </div>
                        <div className="p-3 rounded-lg bg-muted/50 text-center">
                            <p className="text-xs text-muted-foreground mb-1">Market Average</p>
                            <p className="text-2xl font-bold text-foreground">{competitorAvg}%</p>
                        </div>
                        <div className="p-3 rounded-lg bg-emerald-500/10 text-center">
                            <p className="text-xs text-muted-foreground mb-1">Your Best</p>
                            <p className="text-lg font-bold text-emerald-600">{bestAmenity?.name}</p>
                            <p className="text-xs text-emerald-500">{bestAmenity?.score}%</p>
                        </div>
                        <div className="p-3 rounded-lg bg-red-500/10 text-center">
                            <p className="text-xs text-muted-foreground mb-1">Needs Work</p>
                            <p className="text-lg font-bold text-red-600">{worstAmenity?.name}</p>
                            <p className="text-xs text-red-500">{worstAmenity?.score}%</p>
                        </div>
                    </>
                </div> */}

            </div>
        </TooltipProvider>
    );
}